import { useEffect, useState } from "react";
import { readEvidenceBlob, readLearnerWork, saveLearnerReview } from "../data/teacherWorkApi";
import { formatWhen, scoreText } from "./WorkState";
import ui from "./ui.module.css";

// Protected learner work: the bytes come only through the gateway's evidence
// route, after it has checked this teacher's class.
export const EvidenceImage = ({ evidenceId, alt }) => {
  const [state, setState] = useState({ url: null, failed: false });
  useEffect(() => {
    let active = true;
    let objectUrl;
    readEvidenceBlob(evidenceId).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setState({ url: objectUrl, failed: false });
    }, () => active && setState({ url: null, failed: true }));
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [evidenceId]);
  if (state.url) return <img className={ui.workImage} src={state.url} alt={alt} />;
  return <p className={ui.meta}>{state.failed ? "This picture could not be loaded." : "Loading saved work…"}</p>;
};

// The gateway joins several parts of one answer (pairs, groups, points) with
// " · "; one per line reads more clearly than a run-on line. Written answers
// are the learner's own words and are shown exactly, line breaks included.
const Answer = ({ text, written = false }) => {
  if (written) return <p className={ui.preserveText}><strong>{text}</strong></p>;
  const parts = text.split(" · ");
  if (parts.length === 1) return <p><strong>{text}</strong></p>;
  return (
    <ul className={ui.answerList}>
      {parts.map((part, index) => <li key={index}><strong>{part}</strong></li>)}
    </ul>
  );
};

// Objective comparison with the authored answer only (Learner Work Policy v1
// §5). It is never the teacher's judgement, which stays in the review below.
export const OUTCOME = {
  correct: "Correct",
  incorrect: "Incorrect",
  ungraded: "For you to read",
};

const Outcome = ({ outcome }) =>
  OUTCOME[outcome] ? (
    <span className={`${ui.outcome} ${ui[`outcome_${outcome}`] || ""}`}>{OUTCOME[outcome]}</span>
  ) : null;

// Every answer a learner handed in for a step, oldest first, including the
// incorrect ones before a later correct answer (Handoff 007).
const EarlierTries = ({ step }) => {
  const submissions = step.submissions || [];
  if (submissions.length < 2) return null;
  const written = step.type === "text_entry";
  return (
    <div>
      <p className={ui.meta}>All {submissions.length} tries, oldest first</p>
      <ol className={ui.attempts} aria-label={`All ${submissions.length} tries`}>
        {submissions.map((submission, index) => (
          <li key={submission.id || index} className={ui.attemptRow}>
            <span className={ui.meta}>
              Try {index + 1}
              {submission.submittedAt && ` · ${formatWhen(submission.submittedAt)}`}
            </span>
            <Outcome outcome={submission.outcome} />
            {submission.answer && (
              <span className={written ? ui.preserveText : undefined}>{submission.answer}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

// Reconstructed steps, as the gateway words them from the activity definition.
// The latest answer comes first, with its objective outcome; every try,
// incorrect ones included, is listed beneath it.
export const WorkSteps = ({ work, learnerName }) =>
  work.length ? (
    work.map((step, index) => {
      const submissions = step.submissions || [];
      const latest = submissions[submissions.length - 1];
      const earlierIncorrect = submissions.slice(0, -1).filter((s) => s.outcome === "incorrect").length;
      return (
      <section key={step.id || index} className={ui.workStep}>
        <p className={ui.eyebrow}>Step {index + 1}</p>
        {step.prompt && <p>{step.prompt}</p>}
        {step.answer && <Answer text={step.answer} written={step.type === "text_entry"} />}
        {latest && (
          <p className={ui.attemptRow}>
            <Outcome outcome={latest.outcome} />
            {submissions.length > 1 && (
              <span className={ui.meta}>
                {submissions.length} tries
                {earlierIncorrect > 0 && ` · ${earlierIncorrect} incorrect before this`}
              </span>
            )}
          </p>
        )}
        <EarlierTries step={step} />
        {step.evidence.map((item) => (
          <EvidenceImage key={item.id} evidenceId={item.id} alt={`${learnerName}'s work`} />
        ))}
      </section>
      );
    })
  ) : (
    <p>Nothing has been saved yet.</p>
  );

// The Handoff 004 work-and-review view for one piece of learner work.
const LearnerWork = ({ attemptId, onSaved, onDone }) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const [form, setForm] = useState({ status: "draft", judgement: "", score: "", maxScore: "", comment: "" });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let active = true;
    readLearnerWork(attemptId).then((data) => {
      if (!active) return;
      setState({ status: "ready", data });
      if (data.review) setForm({
        status: data.review.status || "draft",
        judgement: data.review.judgement || "",
        score: scoreText(data.review.score),
        maxScore: scoreText(data.review.max_score),
        comment: data.review.comment || "",
      });
    }, () => active && setState({ status: "error", data: null }));
    return () => { active = false; };
  }, [attemptId]);

  const edit = (field) => (event) => {
    setNotice(null);
    setForm({ ...form, [field]: event.target.value });
  };

  const save = async (status) => {
    setSaving(true);
    setNotice(null);
    try {
      await saveLearnerReview(attemptId, { ...form, status });
      setForm((value) => ({ ...value, status }));
      onSaved();
      if (status === "completed") onDone();
      else setNotice("saved");
    } catch (error) {
      setNotice(error?.response?.status === 400 ? "invalid" : "failed");
    } finally { setSaving(false); }
  };

  if (state.status === "loading") return <p role="status">Loading work…</p>;
  if (state.status === "error") return <p className={ui.error} role="alert">This work could not be loaded. Go back and try again.</p>;
  const { data } = state;
  return (
    <div className={ui.stack}>
      <div>
        <h2 className={ui.workLearner}>{data.learner.name}</h2>
        {data.activity?.title && <p className={ui.meta}>{data.activity.title}</p>}
      </div>
      <WorkSteps work={data.work} learnerName={data.learner.name} />
      <fieldset className={ui.reviewForm}>
        <legend>Your review</legend>
        <label>Judgement<input value={form.judgement} onChange={edit("judgement")} maxLength={255} placeholder="For example: Secure" /></label>
        <div className={ui.scoreRow}>
          <label>Score<input type="number" inputMode="decimal" min="0" step="any" value={form.score} onChange={edit("score")} /></label>
          <label>Out of<input type="number" inputMode="decimal" min="0" step="any" value={form.maxScore} onChange={edit("maxScore")} /></label>
        </div>
        <label>Comment<textarea value={form.comment} onChange={edit("comment")} maxLength={2000} /></label>
        {notice === "saved" && <p className={ui.meta} role="status">Draft saved.</p>}
        {notice === "invalid" && <p className={ui.error} role="alert">Check the score: it can't be more than the "Out of" number.</p>}
        {notice === "failed" && <p className={ui.error} role="alert">The review was not saved. Check your connection and try again.</p>}
        <div className={ui.sheetActions}>
          <button type="button" className={`${ui.button} ${ui.secondary}`} disabled={saving} onClick={() => save("draft")}>Save draft</button>
          <button type="button" className={`${ui.button} ${ui.primary}`} disabled={saving} onClick={() => save("completed")}>
            {saving ? "Saving…" : "Mark reviewed"}
          </button>
        </div>
      </fieldset>
    </div>
  );
};

export default LearnerWork;
